import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

import { useCreateOrderMutation } from "@/features/api/paymentApi";

export default function CheckoutPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((s) => s.auth || {});

  /* ---------------- PRODUCT ---------------- */
  const product = useMemo(() => {
    const p = new URLSearchParams(location.search);

    const id = p.get("courseId");
    const slug = p.get("slug");

    if (!id || !slug) return null;

    return {
      id,
      slug,
      name: p.get("courseName") || "Course",
      price: Number(p.get("price")) || 0,
      image: p.get("courseImage") || "",
    };
  }, [location.search]);

  /* ---------------- GUARDS ---------------- */
  useEffect(() => {
    if (!product) {
      toast.error("Invalid checkout link");
      navigate("/", { replace: true });
    }
  }, [product, navigate]);

  useEffect(() => {
    if (isAuthenticated === false) navigate("/login", { replace: true });
  }, [isAuthenticated, navigate]);

  /* ---------------- FORM ---------------- */
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
  });

  const [termsAccepted, setTermsAccepted] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user?.name || "",
        email: user?.email || "",
        phone: user?.phone || user?.mobile || "",
      });
    }
  }, [user]);

  /* ---------------- PRICE ---------------- */
  const price = product?.price || 0;
  const cgst = +(price * 0.09).toFixed(2);
  const sgst = +(price * 0.09).toFixed(2);
  const totalAmount = +(price + cgst + sgst).toFixed(2);

  const [createOrder, { isLoading }] = useCreateOrderMutation();

  /* ---------------- PAYMENT ---------------- */
 const handlePayment = async (e) => {
  e.preventDefault();

  if (!termsAccepted) return toast.error("Accept Terms");

  const phone = formData.phone.replace(/\D/g, "");

  if (!formData.name || !formData.email || !phone)
    return toast.error("All fields required");

  if (!/^\d{10}$/.test(phone))
    return toast.error("Invalid phone");

  try {
    const res = await createOrder({
      userId: user._id,
      name: formData.name,
      email: formData.email,
      phone,
      amount: totalAmount,
      courseId: product.id,
      courseName: product.name,
      productType: "course",
    }).unwrap();

    /* FREE COURSE */
    if (totalAmount === 0) {
      toast.success("Enrollment successful");
      navigate(`/payment/callback?orderId=${res._id}`, { replace: true });
      return;
    }

    /* PAID COURSE */
    if (res?.redirectUrl) {
      window.location.href = res.redirectUrl;
    } else {
      toast.error("Payment gateway failed");
    }

  } catch (err) {
    console.error(err);
    toast.error(err?.data?.message || "Checkout failed");
  }
};

  if (!product) return null;

  return (
    <div className="container mx-auto px-4 py-10 max-w-5xl">
      <form onSubmit={handlePayment} className="grid lg:grid-cols-3 gap-8">

        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Billing Details</CardTitle>
              <CardDescription>Complete your purchase</CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <Input placeholder="Name" value={formData.name}
                  onChange={(e)=>setFormData({...formData,name:e.target.value})}/>
                <Input placeholder="Email" value={formData.email}
                  onChange={(e)=>setFormData({...formData,email:e.target.value})}/>
              </div>

              <Input placeholder="Phone" value={formData.phone}
                onChange={(e)=>setFormData({...formData,phone:e.target.value})}/>

              <div className="flex gap-3">
                <input type="checkbox" checked={termsAccepted}
                  onChange={(e)=>setTermsAccepted(e.target.checked)}/>
                <Label>I accept Terms</Label>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="sticky top-6">
          <CardHeader>
            <CardTitle>Summary</CardTitle>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="flex justify-between font-semibold">
              <span>Total</span>
              <span>₹{totalAmount}</span>
            </div>

            <Button disabled={isLoading || !termsAccepted} className="w-full">
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {totalAmount === 0 ? "Enroll Free" : "Pay Now"}
            </Button>
          </CardContent>
        </Card>

      </form>
    </div>
  );
}